import React, { useEffect, Fragment, useState, useRef } from "react";
import Breadcrumb from "../../../components/common/breadcrumb";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import { showToast } from "../../../components/ToastifyNotification";
import { UncontrolledTooltip } from "reactstrap";
import {
  BulkImportLedgerAccount,
  StoreLedgerAccount
} from "../../../api/masters/ledgerAccountAPI";
import { GetAreas } from "../../../api/masters/areaAPI";
import { GetCities } from "../../../api/masters/cityAPI";
import BulkImport from "../../../components/BulkImport";
import useKeyboardSubmit from "../../../hooks/useKeyboardSubmit";
import useEnterNavigation from "../../../hooks/useEnterNavigation";

// Validation schema using Yup
const schema = yup.object().shape({
  name: yup.string().required("Name is required"),
  shortName: yup.string().required("Short Name is required"),
  underGroup: yup.string().required("Under Group is required"),
  tdsName: yup.string().nullable(),
  depreciationPercentage1: yup
    .number()
    .typeError("Depreciation %1 must be a number")
    .min(0, "Depreciation %1 cannot be negative")
    .max(100, "Depreciation %1 cannot exceed 100")
    .nullable(),
  depreciationPercentage2: yup
    .number()
    .typeError("Depreciation %2 must be a number")
    .min(0, "Depreciation %2 cannot be negative")
    .max(100, "Depreciation %2 cannot exceed 100")
    .nullable(),
  nature: yup.string().nullable(),
  openingBalance: yup
    .number()
    .typeError("Opening balance must be a number")
    .nullable(),
  contactPerson: yup.string().nullable(),
  address: yup.string().nullable(),
  cityId: yup.string().nullable(),
  areaId: yup.string().nullable(),
  phoneNo: yup.string().nullable(),
  mobileNo: yup.string().nullable(),
  fax: yup.string().nullable(),
  email: yup.string().nullable(),
  creditLimit: yup
    .number()
    .typeError("Credit limit must be a number")
    .min(0, "Credit limit cannot be negative")
    .nullable(),
  creditDays: yup
    .number()
    .typeError("Credit days must be a number")
    .min(0, "Credit days cannot be negative")
    .nullable(),
  gstinNo: yup.string().nullable(),
  clientNature: yup.string().nullable(),
  cstNo: yup.string().nullable(),
  lstNo: yup.string().nullable(),
  panNo: yup.string().nullable(),
  tinNo: yup.string().nullable(),
  itWardNo: yup.string().nullable(),
  drugLicenseNo: yup.string().nullable(),
  remark: yup.string().nullable(),
  bank: yup.string().nullable(),
  bankAddress: yup.string().nullable(),
  ifscCode: yup.string().nullable(),
  accountNo: yup.string().nullable()
});

const AddLedgerAccount = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);

  const { register, handleSubmit, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      openingBalance: 0,
      creditLimit: 0,
      creditDays: 0
    }
  });

  const fetchCities = async () => {
    try {
      const response = await GetCities(); // Make sure login function returns token
      if (response.success) {
        setCities(response.data);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    }
  };

  const fetchAreas = async cityId => {
    dispatch({ type: "LOADER_START" });
    try {
      const response = await GetAreas(cityId); // Make sure login function returns token
      if (response.success) {
        setAreas(response.data);
      } else {
        // setError(response.message);
        showToast("error", response.message);
      }
    } catch (error) {
      // setError(error); // Handle login errors
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  // Fetch existing data
  useEffect(() => {
    const loadData = async () => {
      try {
        dispatch({ type: "LOADER_START" });

        await fetchCities();
      } catch (error) {
        showToast("error", error);
      } finally {
        dispatch({ type: "LOADER_STOP" });
      }
    };

    loadData();
  }, []);

  const onSubmit = async data => {
    try {
      dispatch({ type: "LOADER_START" });

      const response = await StoreLedgerAccount(data);

      if (response.success) {
        showToast("success", response.message);
        navigate(`/masters/ledger-account/list`);
      } else {
        showToast("error", response.message);
      }
    } catch (error) {
      showToast("error", error);
    } finally {
      dispatch({ type: "LOADER_STOP" });
    }
  };

  const formRef = useRef(null);
  useKeyboardSubmit(handleSubmit, onSubmit);
  useEnterNavigation(formRef);

  const bulkColumns = [
    { key: "name", label: "Ledger Account Name", sample: "Ledger Account 1" },
    { key: "underGroup", label: "Under Group", sample: "Under Group" },
    { key: "nature", label: "Nature", sample: "Nature" },
    {
      key: "openingBalance",
      label: "Opening Balance",
      sample: "Opening Balance"
    },
    { key: "contactPerson", label: "Contact Person", sample: "Contact Person" },
    { key: "address", label: "Address", sample: "Address" },
    { key: "city", label: "City", sample: "City" },
    { key: "area", label: "Area", sample: "Area" },
    { key: "phoneNo", label: "Phone No", sample: "Phone No" },
    { key: "mobileNo", label: "Mobile No", sample: "Mobile No" },
    { key: "fax", label: "Fax", sample: "Fax" },
    { key: "email", label: "Email", sample: "Email" },
    { key: "creditLimit", label: "Credit Limit", sample: "Credit Limit" },
    { key: "creditDays", label: "Credit Days", sample: "Credit Days" },
    { key: "gstinNo", label: "GSTIN No", sample: "GSTIN No" },
    { key: "clientNature", label: "Client Nature", sample: "Client Nature" }
  ];

  const handleBulkImport = async payload => {
    const response = await BulkImportLedgerAccount(payload);

    if (response.success) {
      showToast("success", response.message);
      navigate("/masters/ledger-account/list");
    } else {
      showToast("error", response.message);
    }
  };

  return (
    <Fragment>
      <Breadcrumb parent="Ledger Account" title="Add Ledger Account" />
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-12">
            <div className="card">
              <div className="card-header">
                <div
                  style={{ display: "flex", justifyContent: "space-between" }}
                >
                  <h5>Add Ledger Account</h5>
                  <Link to={`/masters/ledger-account/list`}>
                    <button className="btn btn-primary" id="list">
                      List Ledger Account
                    </button>
                    <UncontrolledTooltip placement="top" target="list">
                      List Ledger Account
                    </UncontrolledTooltip>
                  </Link>
                </div>
              </div>
              <form
                ref={formRef}
                className="form theme-form"
                onSubmit={handleSubmit(onSubmit)}
              >
                <div className="card-body">
                  <div className="row">
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="name">Name *</label>
                        <input
                          className="form-control"
                          id="name"
                          type="text"
                          placeholder="Name"
                          {...register("name")}
                        />
                        {errors.name &&
                          <p className="text-danger">
                            {errors.name.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="shortName">Short Name *</label>
                        <input
                          className="form-control"
                          id="shortName"
                          type="text"
                          placeholder="Short Name"
                          {...register("shortName")}
                        />
                        {errors.shortName &&
                          <p className="text-danger">
                            {errors.shortName.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="underGroup">Under Group *</label>
                        <input
                          className="form-control"
                          id="underGroup"
                          type="text"
                          placeholder="Under Group"
                          {...register("underGroup")}
                        />
                        {errors.underGroup &&
                          <p className="text-danger">
                            {errors.underGroup.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="tdsName">Tds Name</label>
                        <input
                          className="form-control"
                          id="tdsName"
                          type="text"
                          placeholder="Tds Name"
                          {...register("tdsName")}
                        />
                        {errors.tdsName &&
                          <p className="text-danger">
                            {errors.tdsName.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="depreciationPercentage1">
                          Depreciation Percentage1
                        </label>
                        <input
                          className="form-control"
                          id="depreciationPercentage1"
                          type="number"
                          placeholder="Depreciation Percentage1"
                          {...register("depreciationPercentage1")}
                        />
                        {errors.depreciationPercentage1 &&
                          <p className="text-danger">
                            {errors.depreciationPercentage1.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="depreciationPercentage2">
                          Depreciation Percentage2
                        </label>
                        <input
                          className="form-control"
                          id="depreciationPercentage2"
                          type="number"
                          placeholder="Depreciation Percentage2"
                          {...register("depreciationPercentage2")}
                        />
                        {errors.depreciationPercentage2 &&
                          <p className="text-danger">
                            {errors.depreciationPercentage2.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="nature">Nature</label>
                        <input
                          className="form-control"
                          id="nature"
                          type="text"
                          placeholder="Nature"
                          {...register("nature")}
                        />
                        {errors.nature &&
                          <p className="text-danger">
                            {errors.nature.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="openingBalance">Opening Balance</label>
                        <input
                          className="form-control"
                          id="openingBalance"
                          type="number"
                          placeholder="Opening Balance"
                          {...register("openingBalance")}
                        />
                        {errors.openingBalance &&
                          <p className="text-danger">
                            {errors.openingBalance.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="contactPerson">Contact Person</label>
                        <input
                          className="form-control"
                          id="contactPerson"
                          type="text"
                          placeholder="Contact Person"
                          {...register("contactPerson")}
                        />
                        {errors.contactPerson &&
                          <p className="text-danger">
                            {errors.contactPerson.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="address">Address</label>
                        <textarea
                          className="form-control"
                          id="address"
                          type="text"
                          placeholder="Address"
                          {...register("address")}
                        />
                        {errors.address &&
                          <p className="text-danger">
                            {errors.address.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="cityId">City</label>
                        <select
                          className="form-control"
                          id="cityId"
                          {...register("cityId")}
                          onChange={e => fetchAreas(e.target.value)}
                        >
                          <option value="">Select City</option>
                          {cities.map(city =>
                            <option value={city._id} key={city._id}>
                              {city.name}
                            </option>
                          )}
                        </select>
                        {errors.cityId &&
                          <p className="text-danger">
                            {errors.cityId.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="shortName">Area</label>
                        <select
                          className="form-control"
                          id="areaId"
                          {...register("areaId")}
                        >
                          <option value="">Select Area</option>
                          {areas.map(area =>
                            <option value={area._id} key={area._id}>
                              {area.name}
                            </option>
                          )}
                        </select>
                        {errors.areaId &&
                          <p className="text-danger">
                            {errors.areaId.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="phoneNo">Phone No</label>
                        <input
                          className="form-control"
                          id="phoneNo"
                          type="text"
                          placeholder="Phone No"
                          {...register("phoneNo")}
                        />
                        {errors.phoneNo &&
                          <p className="text-danger">
                            {errors.phoneNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="mobileNo">Mobile No</label>
                        <input
                          className="form-control"
                          id="mobileNo"
                          type="text"
                          placeholder="Mobile No"
                          {...register("mobileNo")}
                        />
                        {errors.mobileNo &&
                          <p className="text-danger">
                            {errors.mobileNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="fax">Fax</label>
                        <input
                          className="form-control"
                          id="fax"
                          type="text"
                          placeholder="Fax"
                          {...register("fax")}
                        />
                        {errors.fax &&
                          <p className="text-danger">
                            {errors.fax.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="email">Email</label>
                        <input
                          className="form-control"
                          id="email"
                          type="text"
                          placeholder="Email"
                          {...register("email")}
                        />
                        {errors.email &&
                          <p className="text-danger">
                            {errors.email.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="creditLimit">Credit Limit</label>
                        <input
                          className="form-control"
                          id="creditLimit"
                          type="number"
                          placeholder="Credit Limit"
                          {...register("creditLimit")}
                        />
                        {errors.creditLimit &&
                          <p className="text-danger">
                            {errors.creditLimit.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="creditDays">Credit Days</label>
                        <input
                          className="form-control"
                          id="creditDays"
                          type="number"
                          placeholder="Credit Days"
                          {...register("creditDays")}
                        />
                        {errors.creditDays &&
                          <p className="text-danger">
                            {errors.creditDays.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="gstinNo">GSTIN No</label>
                        <input
                          className="form-control"
                          id="gstinNo"
                          type="text"
                          placeholder="GSTIN No"
                          {...register("gstinNo")}
                        />
                        {errors.gstinNo &&
                          <p className="text-danger">
                            {errors.gstinNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="clientNature">Client Nature</label>
                        <input
                          className="form-control"
                          id="clientNature"
                          type="text"
                          placeholder="Client Nature"
                          {...register("clientNature")}
                        />
                        {errors.clientNature &&
                          <p className="text-danger">
                            {errors.clientNature.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="cstNo">CST No</label>
                        <input
                          className="form-control"
                          id="cstNo"
                          type="text"
                          placeholder="CST No"
                          {...register("cstNo")}
                        />
                        {errors.cstNo &&
                          <p className="text-danger">
                            {errors.cstNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="lstNo">LST No</label>
                        <input
                          className="form-control"
                          id="lstNo"
                          type="text"
                          placeholder="LST No"
                          {...register("lstNo")}
                        />
                        {errors.lstNo &&
                          <p className="text-danger">
                            {errors.lstNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="panNo">PAN No</label>
                        <input
                          className="form-control"
                          id="panNo"
                          type="text"
                          placeholder="PAN No"
                          {...register("panNo")}
                        />
                        {errors.panNo &&
                          <p className="text-danger">
                            {errors.panNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="tinNo">TIN No</label>
                        <input
                          className="form-control"
                          id="tinNo"
                          type="text"
                          placeholder="TIN No"
                          {...register("tinNo")}
                        />
                        {errors.tinNo &&
                          <p className="text-danger">
                            {errors.tinNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="itWardNo">IT Ward No</label>
                        <input
                          className="form-control"
                          id="itWardNo"
                          type="text"
                          placeholder="IT Ward No"
                          {...register("itWardNo")}
                        />
                        {errors.itWardNo &&
                          <p className="text-danger">
                            {errors.itWardNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="drugLicenseNo">Drug License No</label>
                        <input
                          className="form-control"
                          id="drugLicenseNo"
                          type="text"
                          placeholder="Drug License No"
                          {...register("drugLicenseNo")}
                        />
                        {errors.drugLicenseNo &&
                          <p className="text-danger">
                            {errors.drugLicenseNo.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="remark">Remark</label>
                        <textarea
                          className="form-control"
                          id="remark"
                          type="text"
                          placeholder="Remark"
                          {...register("remark")}
                        />
                        {errors.remark &&
                          <p className="text-danger">
                            {errors.remark.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="bank">Bank</label>
                        <input
                          className="form-control"
                          id="bank"
                          type="text"
                          placeholder="Bank"
                          {...register("bank")}
                        />
                        {errors.bank &&
                          <p className="text-danger">
                            {errors.bank.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="bankAddress">Bank Address</label>
                        <textarea
                          className="form-control"
                          id="bankAddress"
                          type="text"
                          placeholder="Bank Address"
                          {...register("bankAddress")}
                        />
                        {errors.bankAddress &&
                          <p className="text-danger">
                            {errors.bankAddress.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="ifscCode">IFSC Code</label>
                        <input
                          className="form-control"
                          id="ifscCode"
                          type="text"
                          placeholder="IFSC Code"
                          {...register("ifscCode")}
                        />
                        {errors.ifscCode &&
                          <p className="text-danger">
                            {errors.ifscCode.message}
                          </p>}
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="form-group">
                        <label htmlFor="accountNo">Account No</label>
                        <input
                          className="form-control"
                          id="accountNo"
                          type="text"
                          placeholder="Account No"
                          {...register("accountNo")}
                        />
                        {errors.accountNo &&
                          <p className="text-danger">
                            {errors.accountNo.message}
                          </p>}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-footer text-end">
                  <button className="btn btn-primary me-2" type="submit">
                    Submit
                  </button>
                </div>
              </form>
            </div>
            <BulkImport
              columns={bulkColumns}
              onSubmit={handleBulkImport}
              payloadKey="ledgerAccounts"
              sampleFileName="ledgerAccount_sample.csv"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default AddLedgerAccount;
